/**
 * Utility functions to convert Firebase authentication error codes
 * into user-friendly error messages
 */

export interface AuthError {
  code: string;
  message: string;
}

/**
 * Maps Firebase auth error codes to user-friendly messages
 */
const FIREBASE_AUTH_ERROR_MESSAGES: Record<string, string> = {
  // Email/Password Authentication Errors
  'auth/email-already-in-use': 'An account with this email address already exists. Please sign in instead.',
  'auth/weak-password': 'Password is too weak. Please choose a stronger password with at least 6 characters.',
  'auth/invalid-email': 'Please enter a valid email address.',
  'auth/operation-not-allowed': 'Email/password accounts are not enabled. Please contact support.',
  'auth/user-disabled': 'This account has been disabled. Please contact support for assistance.',
  'auth/user-not-found': 'No account found with this email address. Please check your email or sign up.',
  'auth/wrong-password': 'Incorrect password. Please try again.',
  'auth/invalid-credential': 'Invalid email or password. Please check your credentials and try again.',
  'auth/too-many-requests': 'Too many failed attempts. Please wait a few minutes before trying again.',
  'auth/network-request-failed': 'Network error. Please check your internet connection and try again.',
  
  // Email Verification Errors
  'auth/expired-action-code': 'This verification link has expired. Please request a new verification email.',
  'auth/invalid-action-code': 'This verification link is invalid. Please request a new verification email.',
  'auth/user-token-expired': 'Your session has expired. Please sign in again.',
  
  // Google Sign-in Errors
  'auth/popup-closed-by-user': 'Sign-in was cancelled. Please try again.',
  'auth/popup-blocked': 'Pop-up was blocked by your browser. Please allow pop-ups and try again.',
  'auth/cancelled-popup-request': 'Sign-in was cancelled. Please try again.',
  'auth/account-exists-with-different-credential': 'An account already exists with this email using a different sign-in method.',
  
  // General Errors
  'auth/requires-recent-login': 'For security reasons, please sign in again to complete this action.',
  'auth/quota-exceeded': 'Service temporarily unavailable. Please try again later.',
  'auth/unauthorized-domain': 'This domain is not authorized for authentication.',
  'auth/internal-error': 'An internal error occurred. Please try again later.',
  'auth/missing-email': 'Please provide an email address.',
  'auth/missing-password': 'Please provide a password.',
  'auth/invalid-api-key': 'Configuration error. Please contact support.',
  'auth/app-deleted': 'Configuration error. Please contact support.',
  'auth/app-not-authorized': 'Application not authorized. Please contact support.',
  'auth/argument-error': 'Invalid request. Please check your input and try again.',
  'auth/invalid-user-token': 'Your session is invalid. Please sign in again.',
  'auth/user-mismatch': 'The credentials do not match the current user.',
  'auth/credential-already-in-use': 'This credential is already associated with a different account.',
  
  // Custom validation errors
  'auth/invalid-display-name': 'Please enter a valid display name.',
  'auth/display-name-required': 'Display name is required.',
  'auth/password-too-short': 'Password must be at least 6 characters long.',
  'auth/passwords-do-not-match': 'Passwords do not match. Please ensure both passwords are identical.',
  'auth/terms-not-accepted': 'You must accept the Terms of Service to create an account.',
};

/**
 * Maps Firebase Cloud Functions error codes to user-friendly messages
 */
const FIREBASE_FUNCTIONS_ERROR_MESSAGES: Record<string, string> = {
  'functions/cancelled': 'Operation was cancelled. Please try again.',
  'functions/unknown': 'An unknown error occurred. Please try again later.',
  'functions/invalid-argument': 'Invalid input provided. Please check your information and try again.',
  'functions/deadline-exceeded': 'Operation timed out. Please try again.',
  'functions/not-found': 'Requested resource not found.',
  'functions/already-exists': 'Resource already exists.',
  'functions/permission-denied': 'You do not have permission to perform this action.',
  'functions/resource-exhausted': 'Service temporarily unavailable due to high demand. Please try again later.',
  'functions/failed-precondition': 'Operation cannot be completed in the current state.',
  'functions/aborted': 'Operation was aborted. Please try again.',
  'functions/out-of-range': 'Invalid input range.',
  'functions/unimplemented': 'This feature is not yet available.',
  'functions/internal': 'An internal server error occurred. Please try again later.',
  'functions/unavailable': 'Service is temporarily unavailable. Please try again later.',
  'functions/data-loss': 'Data corruption detected. Please contact support.',
  'functions/unauthenticated': 'Please sign in to continue.',
};

/**
 * Converts Firebase error codes to user-friendly messages
 */
export function getAuthErrorMessage(error: any): string {
  if (!error) {
    return 'An unexpected error occurred. Please try again.';
  }

  // Handle string errors directly
  if (typeof error === 'string') {
    return error;
  }

  // Handle Firebase Auth errors
  if (error.code && error.code.startsWith('auth/')) {
    return FIREBASE_AUTH_ERROR_MESSAGES[error.code] || error.message || 'Authentication failed. Please try again.';
  }

  // Handle Firebase Functions errors
  if (error.code && error.code.startsWith('functions/')) {
    return FIREBASE_FUNCTIONS_ERROR_MESSAGES[error.code] || error.message || 'Service error. Please try again later.';
  }

  // Handle errors with custom messages
  if (error.message) {
    // Check if the message contains known error patterns
    const message = error.message.toLowerCase();
    
    if (message.includes('email-already-in-use') || message.includes('email already exists')) {
      return 'An account with this email address already exists. Please sign in instead.';
    }
    if (message.includes('weak-password') || message.includes('password should be at least')) {
      return 'Password is too weak. Please choose a stronger password with at least 6 characters.';
    }
    if (message.includes('invalid-email') || message.includes('badly formatted')) {
      return 'Please enter a valid email address.';
    }
    if (message.includes('user-not-found')) {
      return 'No account found with this email address. Please check your email or sign up.';
    }
    if (message.includes('wrong-password') || message.includes('invalid-credential')) {
      return 'Invalid email or password. Please check your credentials and try again.';
    }
    if (message.includes('too-many-requests')) {
      return 'Too many failed attempts. Please wait a few minutes before trying again.';
    }
    if (message.includes('network')) {
      return 'Network error. Please check your internet connection and try again.';
    }
    
    // Return the original message if it's already user-friendly
    return error.message;
  }

  // Fallback for unknown errors
  return 'An unexpected error occurred. Please try again.';
}

/**
 * Validates authentication input and returns user-friendly error messages
 */
export function validateAuthInput(data: {
  email?: string;
  password?: string;
  confirmPassword?: string;
  displayName?: string;
}): Record<string, string> {
  const errors: Record<string, string> = {};

  // Email validation
  if (data.email !== undefined) {
    if (!data.email) {
      errors.email = 'Email address is required.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
      errors.email = 'Please enter a valid email address.';
    }
  }

  // Password validation
  if (data.password !== undefined) {
    if (!data.password) {
      errors.password = 'Password is required.';
    } else if (data.password.length < 6) {
      errors.password = 'Password must be at least 6 characters long.';
    }
  }

  // Confirm password validation
  if (data.confirmPassword !== undefined && data.password !== undefined) {
    if (!data.confirmPassword) {
      errors.confirmPassword = 'Please confirm your password.';
    } else if (data.password !== data.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match.';
    }
  }

  // Display name validation
  if (data.displayName !== undefined) {
    if (!data.displayName) {
      errors.displayName = 'Display name is required.';
    } else if (data.displayName.length < 2) {
      errors.displayName = 'Display name must be at least 2 characters long.';
    } else if (data.displayName.length > 50) {
      errors.displayName = 'Display name must be less than 50 characters.';
    }
  }

  return errors;
}

/**
 * Checks if an error indicates the user should try signing in instead of signing up
 */
export function shouldRedirectToSignIn(error: any): boolean {
  if (!error?.code) return false;
  
  return error.code === 'auth/email-already-in-use' || 
         error.code === 'functions/already-exists' ||
         (error.message && error.message.toLowerCase().includes('already exists'));
}

/**
 * Checks if an error indicates a temporary issue that the user should retry
 */
export function isRetryableError(error: any): boolean {
  if (!error?.code) return false;
  
  const retryableCodes = [
    'auth/network-request-failed',
    'auth/too-many-requests',
    'functions/deadline-exceeded',
    'functions/resource-exhausted',
    'functions/unavailable',
    'functions/internal'
  ];
  
  return retryableCodes.includes(error.code);
}